import {
  ApplicationRef,
  createComponent,
  EnvironmentInjector,
  Injectable,
  Injector,
} from '@angular/core';
import { FlyoutAnimationComponent } from './flyout-animation/flyout-animation.component';
import {
  fliesToAvatar,
  fliesToPrizeUnlocker,
  type GameHudPrizeType,
} from './game-page-prize-animation.helper';

export interface FlyoutShowParams {
  lottiePath: string;
  delta: number;
  displayLabel: string;
}

/** Maximum time (ms) to wait for a HUD icon element to appear in the DOM. */
const HUD_ICON_WAIT_MS = 5000;

@Injectable({ providedIn: 'root' })
export class FlyoutRendererService {
  constructor(
    private appRef: ApplicationRef,
    private injector: Injector,
    private envInjector: EnvironmentInjector,
  ) { }

  private hudTargetSelectors(type: string): string[] {
    if (fliesToPrizeUnlocker(type)) {
      return [
        `[data-hud-icon="${type}"]`,
        '[data-hud-icon="prize_unlock_target"]',
        '.badge-container',
      ];
    }
    if (fliesToAvatar(type as GameHudPrizeType) || type === 'free_play') {
      return [
        `[data-hud-icon="${type}"]`,
        '.avatar-wrapper',
      ];
    }
    // Prize-box: white count pill (ticket icon + number) on the purple badge
    if (type === 'star_token') {
      return [
        '[data-hud-icon="star_token"]',
        '.badge.prize-badge span.count',
        '.badge.prize-badge',
      ];
    }
    return [`[data-hud-icon="${type}"]`];
  }

  private findHudTarget(type: string): HTMLElement | null {
    for (const selector of this.hudTargetSelectors(type)) {
      const el = document.querySelector<HTMLElement>(selector);
      if (el) {
        return el;
      }
    }
    return null;
  }

  private waitForHudIcon(type: string): Promise<HTMLElement | null> {
    return new Promise((resolve) => {
      const deadline = performance.now() + HUD_ICON_WAIT_MS;

      const poll = () => {
        const el = this.findHudTarget(type);
        if (el) {
          resolve(el);
          return;
        }
        if (performance.now() >= deadline) {
          resolve(null);
          return;
        }
        requestAnimationFrame(poll);
      };

      requestAnimationFrame(poll);
    });
  }

  show(type: string, lottieUrl: string, delta: number) {
    void this.showAndWait(type, {
      lottiePath: lottieUrl,
      delta,
      displayLabel: `+${delta}`,
    });
  }

  async showAndWait(type: string, params: FlyoutShowParams): Promise<void> {
    const cmpRef = createComponent(FlyoutAnimationComponent, {
      environmentInjector: this.envInjector,
      elementInjector: this.injector,
    });

    // Set type/delta before lottiePath — the path setter loads immediately.
    cmpRef.instance.type = type;
    cmpRef.instance.delta = params.delta;
    cmpRef.instance.displayLabel = params.displayLabel;
    cmpRef.instance.lottiePath = params.lottiePath;

    const hudCoinEl = await this.waitForHudIcon(type);

    this.appRef.attachView(cmpRef.hostView);
    const hostEl = cmpRef.location.nativeElement as HTMLElement;

    this.positionFlyoutAtHudIcon(hostEl, hudCoinEl, type);
    document.body.appendChild(hostEl);

    return new Promise<void>((resolve) => {
      const sub = cmpRef.instance.done.subscribe(() => {
        sub.unsubscribe();
        this.appRef.detachView(cmpRef.hostView);
        cmpRef.destroy();
        resolve();
      });
    });
  }

  /**
   * Figma Free Games reference — streams from mid-screen:
   *   LEFT  (up-left):  points, gems, free_play → left HUD / avatar
   *   RIGHT (up-right): star_token, rapid_fire_token, cash/credit → right HUD / unlocker
   * Icons ≈ header balance icon size; paths fan into each HUD target.
   */
  private positionFlyoutAtHudIcon(
    hostEl: HTMLElement,
    hudCoinEl: HTMLElement | null,
    type: string,
  ): void {
    const viewportW = typeof window !== 'undefined' ? window.innerWidth : 390;
    const viewportH = typeof window !== 'undefined' ? window.innerHeight : 700;
    const aim = this.flyoutAim(type);

    // Figma: flying icons ≈ header balance icon (~28–36px). Artboard particles
    // are ~10% of large assets → host ~140–160 (not 220–260).
    const baseSize = Math.round(Math.min(160, Math.max(140, viewportW * 0.4)));
    const size = Math.round(baseSize * aim.sizeScale);
    const scale = 1;

    hostEl.style.position = 'fixed';
    hostEl.style.width = `${size}px`;
    hostEl.style.height = `${size}px`;
    hostEl.style.zIndex = '9999';
    hostEl.style.pointerEvents = 'none';
    hostEl.style.margin = '0';
    hostEl.style.overflow = 'visible';
    hostEl.style.transformOrigin = 'top center';
    hostEl.classList.toggle('flyout-mirror-x', aim.mirrorX);

    const rect = hudCoinEl?.getBoundingClientRect();
    const hasRect = !!rect && (rect.width > 0 || rect.height > 0 || rect.top > 0 || rect.left > 0);

    if (!hasRect || !rect) {
      hostEl.style.left = '50%';
      hostEl.style.top = `${Math.round(viewportH * 0.1)}px`;
      hostEl.style.transform = `translate(-50%, 0) scale(${scale})`;
      return;
    }

    const targetCx = rect.left + Math.max(rect.width, 1) / 2;
    const targetCy = rect.top + Math.max(rect.height, 1) / 2;

    // star_token: land on the ticket icon (left side of the white count pill), not pill center
    let aimCx = targetCx;
    let aimCy = targetCy;
    if (type === 'star_token') {
      // Ticket sits left in the pill; bias slightly right so the path end hits the icon
      aimCx = rect.left + Math.max(rect.width, 1) * 0.38;
      aimCy = rect.top + Math.max(rect.height, 1) / 2;
    }

    // Align Lottie landing point on HUD; lower host hangs toward screen center (burst)
    // Mirrored paths: landX is the *visible* end after scaleX(-1) (~right side of host)
    let left = aimCx - size * aim.landX;
    let top = aimCy - size * aim.landY;

    left = Math.min(Math.max(left, -size * 0.2), viewportW - size * 0.8);
    top = Math.min(Math.max(top, -size * 0.06), viewportH * 0.38);

    hostEl.style.left = `${Math.round(left)}px`;
    hostEl.style.top = `${Math.round(top)}px`;
    hostEl.style.transform = `scale(${scale})`;
  }

  /**
   * Per-type aim from Figma screenshot.
   * mirrorX: flip authored up-left Lottie → up-right for right-HUD prizes.
   * landX/Y: fraction of host where the path ends (aligned to HUD target).
   * sizeScale: keep icons roughly uniform (cash bills slightly larger in art).
   */
  private flyoutAim(type: string): {
    mirrorX: boolean;
    landX: number;
    landY: number;
    sizeScale: number;
  } {
    const t = type as GameHudPrizeType;

    switch (type) {
      // —— LEFT stream (up-left) ——
      // Path ends on the left side of the Lottie; pin that end on the coin/gem icon
      // (landX 0.42 left particles sitting between avatar and the icons).
      case 'points':
        return { mirrorX: false, landX: 0.18, landY: 0.22, sizeScale: 1 };
      case 'gems':
        return { mirrorX: false, landX: 0.18, landY: 0.22, sizeScale: 1 };
      case 'cash':
      case 'winning_balance':
      case 'credit':
        // Bills → prize-unlocker badge (right stream, mirrored)
        return { mirrorX: true, landX: 0.72, landY: 0.22, sizeScale: 1 };
      case 'free_play':
        // Free play → profile avatar
        return { mirrorX: false, landX: 0.48, landY: 0.14, sizeScale: 1 };

      // —— RIGHT stream (up-right) ——
      // Mirrored: visible path end is on the RIGHT of the host (~0.78), not 0.18
      case 'star_token':
        return { mirrorX: true, landX: 0.72, landY: 0.28, sizeScale: 1 };
      case 'rapid_fire_token':
        // Flame tokens → RF / entries pill (further right than prize-box)
        return { mirrorX: true, landX: 0.6, landY: 0.14, sizeScale: 1 };

      default:
        if (fliesToPrizeUnlocker(type)) {
          return { mirrorX: true, landX: 0.72, landY: 0.22, sizeScale: 1 };
        }
        if (fliesToAvatar(t)) {
          return { mirrorX: false, landX: 0.48, landY: 0.14, sizeScale: 1 };
        }
        return { mirrorX: false, landX: 0.5, landY: 0.18, sizeScale: 1 };
    }
  }
}
